// Cleaning, in this order: duplicate bursts, test accounts, ghost "created" twins, date cutoff.
import { config } from '../config';
import { defaultRoleOf } from './appointment';
import { tsOf } from './dates';
import type { Appointment, RoleOf } from './types';

const TEST_RE = /test/i;

/** Substring, not word: "testh testh" and "Testeur" match. Only the email's local part is checked. */
export function isTestAccount(a: Appointment): boolean {
  const u = a.simpleUser || {};
  const hay = [u.firstName, u.lastName, u.label, String(u.email || '').split('@')[0]].filter(Boolean).join(' ');
  return TEST_RE.test(hay);
}

/**
 * Same customer, same UTC day: a "created" raised at or before a "completed" one is the
 * leftover half of a double booking. A "created" raised after it is a real new booking.
 */
export function removeGhostCreated(list: Appointment[]): Appointment[] {
  const groups = new Map<string, Appointment[]>();
  list.forEach((a) => {
    if (!a.createdAt) return;
    const d = new Date(a.createdAt);
    if (isNaN(d.getTime())) return;
    const key =
      (a.simpleUserId || (a.simpleUser && a.simpleUser.id) || '?') +
      '|' + d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate();
    const g = groups.get(key);
    if (g) g.push(a);
    else groups.set(key, [a]);
  });
  const ghost = new Set<Appointment>();
  groups.forEach((g) => {
    if (g.length < 2) return;
    let latest = -Infinity;
    g.forEach((a) => {
      if (String(a.status || '').toLowerCase() !== 'completed') return;
      const t = new Date(a.createdAt as string).getTime();
      if (!isNaN(t) && t > latest) latest = t;
    });
    if (latest === -Infinity) return;
    g.forEach((a) => {
      if (String(a.status || '').toLowerCase() !== 'created') return;
      const t = new Date(a.createdAt as string).getTime();
      if (!isNaN(t) && t <= latest) ghost.add(a);
    });
  });
  return ghost.size ? list.filter((a) => !ghost.has(a)) : list;
}

export interface DedupeResult {
  list: Appointment[];
  removed: number;
  /** customers with at least one burst */
  groups: number;
  /** rows dropped because a sibling had a better outcome */
  superseded: number;
}

/**
 * Duplicate bursts: per customer, sorted by createdAt, rows at the same location code within
 * `windowMs` of the row that opened the burst are one booking. The survivor is the best
 * outcome (collected > pending > ignored), then the latest touched, then the earliest created.
 */
export function dedupe(arr: Appointment[], windowMs: number, roleOf: RoleOf = defaultRoleOf): DedupeResult {
  const rank = (a: Appointment) => {
    const r = roleOf(a.status);
    return r === 'collected' ? 3 : r === 'pending' ? 2 : 1;
  };
  const beats = (a: Appointment, b: Appointment) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra > rb;
    const ua = tsOf(a.updatedAt || a.createdAt), ub = tsOf(b.updatedAt || b.createdAt);
    if (ua !== ub) return ua > ub;
    return tsOf(a.createdAt) < tsOf(b.createdAt);
  };
  // Insertion order of the keys matters for the output order: same as the legacy object.
  const byUser: Record<string, Appointment[]> = {};
  arr.forEach((a, i) => {
    const uid = a.simpleUserId || '__no_user_' + i;
    (byUser[uid] = byUser[uid] || []).push(a);
  });
  const kept: Appointment[] = [];
  let groups = 0, superseded = 0;
  Object.keys(byUser).forEach((uid) => {
    const list = byUser[uid].slice().sort((x, y) => tsOf(x.createdAt) - tsOf(y.createdAt));
    const bursts: { code: unknown; anchor: number; items: Appointment[] }[] = [];
    let cur: (typeof bursts)[number] | null = null;
    list.forEach((a) => {
      const t = tsOf(a.createdAt);
      if (cur && a.locationCode === cur.code && t - cur.anchor <= windowMs) cur.items.push(a);
      else {
        cur = { code: a.locationCode, anchor: t, items: [a] };
        bursts.push(cur);
      }
    });
    let had = false;
    bursts.forEach((b) => {
      if (b.items.length === 1) {
        kept.push(b.items[0]);
        return;
      }
      had = true;
      let win = b.items[0];
      b.items.forEach((a) => {
        if (beats(a, win)) win = a;
      });
      kept.push(win);
      b.items.forEach((a) => {
        if (a !== win && rank(a) !== rank(win)) superseded++;
      });
    });
    if (had) groups++;
  });
  return { list: kept, removed: arr.length - kept.length, groups, superseded };
}

/** Duplicate window the pages start with (seconds); each page lets the user change it. */
export const DEFAULT_DUP_WINDOW_SEC = 7500;

export interface CleanOptions {
  windowMs?: number;
  roleOf?: RoleOf;
  removeTest?: boolean;
  removeGhost?: boolean;
  /** yyyy-mm-dd; undefined = the site's "data starts from"; null or '' = no cutoff */
  cutoff?: string | null;
}
export interface CleanResult {
  list: Appointment[];
  dup: number;
  test: number;
  ghost: number;
  excluded: number;
  dedupe: DedupeResult;
  testNames: string[];
}

/** The whole pipeline, with the tools' default settings unless told otherwise. */
export function clean(arr: Appointment[], opts: CleanOptions = {}): CleanResult {
  const win = opts.windowMs === undefined ? 2000 : opts.windowMs;
  const dd = dedupe(arr, win, opts.roleOf);
  let list = dd.list, test = 0, ghost = 0;
  const testNames = new Set<string>();
  if (opts.removeTest !== false) {
    const b = list.length;
    list = list.filter((a) => {
      if (!isTestAccount(a)) return true;
      const u = a.simpleUser;
      testNames.add(((u?.firstName || '') + ' ' + (u?.lastName || '')).trim() || '—');
      return false;
    });
    test = b - list.length;
  }
  if (opts.removeGhost !== false) {
    const b2 = list.length;
    list = removeGhostCreated(list);
    ghost = b2 - list.length;
  }
  const cutoff = opts.cutoff === undefined ? config.dataStartsFrom : opts.cutoff;
  let excluded = 0;
  if (cutoff) {
    const c = new Date(cutoff + 'T00:00:00.000Z').getTime();
    const b3 = list.length;
    list = list.filter((a) => {
      if (!a.createdAt) return true;
      const t = new Date(a.createdAt).getTime();
      return isNaN(t) ? true : t >= c;
    });
    excluded = b3 - list.length;
  }
  return { list, dup: dd.removed, test, ghost, excluded, dedupe: dd, testNames: [...testNames].sort() };
}
